/**
 * AI Analysis Service
 * Handles communication with BugMail's AI analysis backend
 */

import { version } from '../../package.json';

class AIAnalysisService {
  /**
   * Initialize the AI Analysis Service
   * @param {Object} config - Configuration options
   * @param {string} config.endpoint - BugMail API endpoint
   * @param {string} config.apiKey - BugMail API key
   */
  constructor(config) {
    this.endpoint = config.endpoint;
    this.apiKey = config.apiKey;
    this.explanationCache = new Map();
  }

  /**
   * Get AI analysis for an error
   * @param {Error} error - The error object
   * @param {Object} context - Additional context about the error
   * @returns {Promise<Object>} AI analysis results
   */
  async analyzeError(error, context = {}) {
    const errorKey = this._generateErrorKey(error, context);
    
    // Check cache first
    if (this.explanationCache.has(errorKey)) {
      return this.explanationCache.get(errorKey);
    }

    try {
      // *** Correct the endpoint path to /api/v1/ai/analyze ***
      const response = await fetch(`${this.endpoint}/api/v1/ai/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // *** Use X-BugMail-API-Key header for consistency with other SDK requests ***
          'X-BugMail-API-Key': this.apiKey, 
          'X-BugMail-SDK-Version': version
        },
        body: JSON.stringify({
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
            cause: error.cause
          },
          context: {
            ...context,
            url: window.location.href,
            timestamp: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error(`AI Analysis failed: ${response.statusText}`);
      }

      const analysis = await response.json();
      
      // Cache the result with timestamp
      this.explanationCache.set(errorKey, {
        ...analysis,
        timestamp: Date.now()
      });
      
      return analysis;
    } catch (err) {
      console.warn('[BugMail] AI Analysis failed:', err);
      return null;
    }
  }

  /**
   * Generate a cache key for an error
   * @param {Error} error - The error object
   * @param {Object} context - Error context
   * @returns {string} Cache key
   * @private
   */
  _generateErrorKey(error, context) {
    const errorInfo = {
      name: error.name,
      message: error.message,
      stack: error.stack?.split('\n')[0] || '',
      contextKeys: Object.keys(context).sort()
    };
    return JSON.stringify(errorInfo);
  }

  /**
   * Clear the explanation cache
   * @public
   */
  clearCache() {
    this.explanationCache.clear();
  }

  /**
   * Set maximum age for cached explanations
   * @param {number} maxAgeMs - Maximum age in milliseconds
   * @public
   */
  setMaxCacheAge(maxAgeMs) {
    // Clear expired entries
    const now = Date.now();
    for (const [key, value] of this.explanationCache.entries()) {
      if (now - value.timestamp > maxAgeMs) {
        this.explanationCache.delete(key);
      }
    }
  }
}

// Export the AIAnalysisService as default
export default AIAnalysisService;